// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DateTimeCalendar.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Moment from 'moment';
import 'moment-range';
import { get, head, last } from 'lodash/fp';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import DateTimeCell from './Izenda.DateTimeCell';
import DateTimeNav from './Izenda.DateTimeNav';
import { Levels, Units } from './Izenda.DateTimeConstants';

const preYears = 4;
const nextYears = 7;
const firstWeekday = 0;
const lastWeekday = 6;

export default class DateTimeCalendar extends Component {
  static propTypes = {
    datetime: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    level: PropTypes.string.isRequired,
    setLevel: PropTypes.func.isRequired,
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func
  };

  componentDidMount() {
    this.scrollToHour();
  }

  componentDidUpdate() {
    if (!this.props.above()) {
      this.scrollToHour();
    }
  }

  scrollToHour = () => {
    if (this.props.level == 'hours' && this.datetimeCellRef) {
      const { parentNode } = this.datetimeCellRef.parentNode;
      const deltaTop = 6;
      parentNode.scrollTop = parentNode.offsetTop - deltaTop;
    }
  };

  onNavigateCell = (datetime: Date) => {
    const lvl = Levels[this.props.level];
    if (lvl.down) this.props.setLevel(lvl.down);
    this.props.onSelect(datetime, !lvl.down, lvl.key);
  };

  onNavigateUp = () => {
    const lvl = Levels[this.props.level];
    if (lvl.up) this.props.setLevel(lvl.up);
  };

  onNavigateLeft = () => {
    const lvl = Levels[this.props.level].nav;
    this.props.onSelect(this.props.datetime.subtract(lvl.span, lvl.unit));
  };

  onNavigateRight = () => {
    const lvl = Levels[this.props.level].nav;
    this.props.onSelect(this.props.datetime.add(lvl.span, lvl.unit));
  };

  onToday = () => {
    const lvl = Levels[this.props.level];
    if (Moment(this.props.datetime).isSame(Moment(), 'day')) {
      this.props.onSelect(Moment(), !lvl.down);
    } else {
      this.props.onSelect(Moment());
    }
  };

  getTitle = (unit: string, datetime: Date) => {
    datetime = datetime || Moment();
    switch (unit) {
      case 'years': {
        const start = datetime.clone().subtract(preYears, 'years');
        const end = datetime.clone().add(nextYears, 'years');
        let years = [];

        Moment()
          .range(start, end)
          .by(Units.YEAR, function(year) {
            years.push({
              label: year.format('YYYY'),
              selected: year.isSame(datetime, 'year')
            });
          });
        return [head(years).label, last(years).label].join('-');
      }
      case 'months':
        return datetime.format('YYYY');

      case 'days':
        return datetime.format('MMMM');

      case 'hours':
        return null;
    }
  };

  getCells = (unit: string, datetime: Date) => {
    datetime = datetime || Moment();
    let start, end, format;
    switch (unit) {
      case 'years': {
        start = datetime.clone().subtract(preYears, 'years');
        end = datetime.clone().add(nextYears, 'years');
        format = get('options.format.year', this.props) || 'YYYY';
        let years = [];

        Moment()
          .range(start, end)
          .by(Units.YEAR, function(year) {
            years.push({
              moment: year,
              label: year.format(format),
              selected: year.isSame(datetime, 'year')
            });
          });

        return years;
      }

      case 'months': {
        start = datetime.clone().startOf('year');
        end = datetime.clone().endOf('year');
        format = get('options.format.month', this.props) || 'MM';
        let months = [];

        Moment()
          .range(start, end)
          .by(Units.MONTH, function(month) {
            months.push({
              moment: month,
              label: month.format(format),
              selected: month.isSame(datetime, 'month')
            });
          });

        return months;
      }

      case 'days': {
        start = datetime
          .clone()
          .startOf('month')
          .weekday(firstWeekday);
        end = datetime
          .clone()
          .endOf('month')
          .weekday(lastWeekday);
        format = get('options.format.day', this.props) || 'DD';
        let days = [];

        Moment.weekdaysMin().forEach(function(day) {
          days.push({
            label: day,
            header: true
          });
        });

        Moment()
          .range(start, end)
          .by(Units.DAY, function(day) {
            days.push({
              moment: day,
              label: day.format(format),
              past: day.isBefore(datetime, 'month'),
              future: day.isAfter(datetime, 'month'),
              selected: day.isSame(datetime, 'day'),
              today: day.isSame(Moment(), 'day')
            });
          });

        return days;
      }

      case 'hours': {
        start = datetime.clone().startOf('day');
        end = datetime.clone().endOf('day');
        format = get('options.format.hour', this.props) || 'HH:mm:ss';

        let hours = [];
        const deltaCloseMinutes = 31;
        const closeBefore = datetime.clone().subtract(deltaCloseMinutes, 'minutes');
        const closeAfter = datetime.clone().add(deltaCloseMinutes, 'minutes');

        Moment()
          .range(start, end)
          .by(Units.HOUR, function(hour) {
            hours.push({
              moment: hour,
              label: hour.format(format),
              selected: hour.isSame(datetime, 'minute'),
              nearestBefore: hour.isBetween(closeBefore, datetime),
              nearestAfter: hour.isBetween(datetime, closeAfter)
            });
            const deltaHalfHour = 30;
            const halfHour = hour.clone().add(deltaHalfHour, 'minutes');
            hours.push({
              moment: halfHour,
              label: halfHour.format(format),
              selected: halfHour.isSame(datetime, 'minute'),
              nearestBefore: halfHour.isBetween(closeBefore, datetime),
              nearestAfter: halfHour.isBetween(datetime, closeAfter)
            });
          });

        return hours;
      }
    }
  };

  handleClick = (value: any) => () => {
    this.props.above && this.props.above(value);
  };

  updateDateTimeCellRef = cell => dom => {
    this.datetimeCellRef = cell.selected || cell.nearestBefore ? dom : null;
  };

  render() {
    const props = this.props;
    const { datetime, id, level } = props;

    return (
      <div className='izenda-DateTimeCalendar' onMouseDown={this.handleClick(true)} onMouseUp={this.handleClick(false)}>
        {level === 'hours' && (
          <div className='izenda-DateTimeCalendar-hours'>
            <ScrollBox>
              {this.getCells(level, datetime).map(
                function(cell, i) {
                  return (
                    <DateTimeCell
                      key={i}
                      ref={this.updateDateTimeCellRef(cell)}
                      label={cell.label}
                      level='hours'
                      type='base'
                      selected={cell.selected}
                      today={cell.today}
                      moment={cell.moment}
                      onClick={this.onNavigateCell}
                      invalid={props.validate(cell.moment, level)}
                    />
                  );
                }.bind(this)
              )}
            </ScrollBox>
          </div>
        )}
        {level !== 'hours' && (
          <div className='izenda-DateTimeCalendar-container'>
            <DateTimeNav
              id={id}
              onPrev={this.onNavigateLeft}
              onNext={this.onNavigateRight}
              onTitle={this.onNavigateUp}
              title={this.getTitle(level, datetime)}
            />
            <div className={classnames('izenda-DateTimeCalendar-content', level)}>
              {this.getCells(level, datetime).map((cell, i) => {
                let type;
                switch (true) {
                  case cell.header:
                    type = 'header';
                    break;
                  case cell.past:
                    type = 'past';
                    break;
                  case cell.future:
                    type = 'future';
                    break;
                  default:
                    type = 'base';
                    break;
                }
                return (
                  <DateTimeCell
                    key={i}
                    ref={this.updateDateTimeCellRef(cell)}
                    label={cell.label}
                    level={level}
                    type={type}
                    selected={cell.selected}
                    today={cell.today}
                    moment={cell.moment}
                    onClick={this.onNavigateCell}
                    invalid={props.validate(cell.moment, level)}
                  />
                );
              })}

              <div onClick={this.onToday} className='izenda-DateTimeCell-today'>
                {get('options.format.today', props) || 'Today'}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/DateTime/Izenda.DateTimeCalendar.jsx